import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';

interface LandingNavProps {
  onGetStarted: () => void;
}

export function LandingNav({ onGetStarted }: LandingNavProps) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [solutionsOpen, setSolutionsOpen] = useState(false);
  const solutionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 64);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (solutionsRef.current && !solutionsRef.current.contains(e.target as Node)) {
        setSolutionsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <>
      <nav
        id="navbar"
        className={`fixed top-0 w-full z-50 transition-all duration-500 ${
          scrolled ? 'py-4' : 'py-6'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <img 
              src="/logo.webp" 
              alt="VirtualStagePro" 
              className="w-10 h-10 rounded-xl shadow-soft transition-transform duration-500 group-hover:-translate-y-0.5"
            />
            <span className="hidden sm:block text-xl font-serif font-medium tracking-tight text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.3)]">
              VirtualStagePro
            </span>
          </Link>

          {/* Floating Pill Navigation - Desktop */}
          <div
            className={`hidden md:flex items-center gap-6 px-8 py-3 rounded-full border shadow-soft transition-all duration-500 ${
              scrolled
                ? 'bg-alabaster/95 backdrop-blur-xl border-stone/80'
                : 'bg-alabaster/70 backdrop-blur-xl border-stone/50'
            }`}
          >
            <Link
              to="/virtual-staging"
              className="text-sm font-medium text-sand hover:text-olive transition-colors duration-300"
            >
              Virtual Staging
            </Link>
            <Link
              to="/virtual-staging/styles"
              className="text-sm font-medium text-sand hover:text-olive transition-colors duration-300"
            >
              Styles
            </Link>
            
            {/* Solutions Dropdown */}
            <div ref={solutionsRef} className="relative">
              <button
                onClick={() => setSolutionsOpen(!solutionsOpen)}
                className="flex items-center gap-1 text-sm font-medium text-sand hover:text-olive transition-colors duration-300"
              >
                Solutions
                <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${solutionsOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {solutionsOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-56 bg-alabaster/95 backdrop-blur-xl border border-stone rounded-2xl shadow-soft-lg overflow-hidden">
                  <div className="py-2">
                    <div className="px-4 py-2 text-xs font-mono uppercase tracking-wider text-sand/60">
                      By Role
                    </div>
                    <Link
                      to="/virtual-staging/real-estate-agents"
                      onClick={() => setSolutionsOpen(false)}
                      className="block px-4 py-2.5 text-sm text-sand hover:text-olive hover:bg-stone/30 transition-colors"
                    >
                      Real Estate Agents
                    </Link>
                    <Link
                      to="/virtual-staging/photographers"
                      onClick={() => setSolutionsOpen(false)}
                      className="block px-4 py-2.5 text-sm text-sand hover:text-olive hover:bg-stone/30 transition-colors"
                    >
                      Photographers
                    </Link>
                    <Link
                      to="/virtual-staging/broker-teams"
                      onClick={() => setSolutionsOpen(false)}
                      className="block px-4 py-2.5 text-sm text-sand hover:text-olive hover:bg-stone/30 transition-colors"
                    >
                      Broker Teams
                    </Link>
                    <div className="my-2 border-t border-stone/50" />
                    <div className="px-4 py-2 text-xs font-mono uppercase tracking-wider text-sand/60">
                      Partners
                    </div>
                    <Link
                      to="/partners/photographers"
                      onClick={() => setSolutionsOpen(false)}
                      className="block px-4 py-2.5 text-sm text-sand hover:text-olive hover:bg-stone/30 transition-colors"
                    >
                      Photographer Partners
                    </Link>
                    <Link
                      to="/partners/brokerages"
                      onClick={() => setSolutionsOpen(false)}
                      className="block px-4 py-2.5 text-sm text-sand hover:text-olive hover:bg-stone/30 transition-colors"
                    >
                      Brokerage Partners
                    </Link>
                  </div>
                </div>
              )}
            </div>
            
            <Link
              to="/virtual-staging/before-after"
              className="text-sm font-medium text-sand hover:text-olive transition-colors duration-300"
            >
              Before/After
            </Link>
            <Link
              to="/virtual-staging/pricing"
              className="text-sm font-medium text-sand hover:text-olive transition-colors duration-300"
            >
              Pricing
            </Link>
          </div>

          {/* CTA Buttons */}
          <div className="flex items-center gap-4">
            <Link
              to="/auth"
              className="hidden sm:block text-sm font-medium text-white/90 hover:text-terracotta transition-colors duration-300 drop-shadow-[0_1px_2px_rgba(0,0,0,0.3)]"
            >
              Log in
            </Link>
            <button
              onClick={onGetStarted}
              className="hidden sm:block bg-charcoal hover:bg-olive text-alabaster px-6 py-3 rounded-full text-sm font-medium transition-all duration-500 shadow-lg hover:-translate-y-0.5 hover:shadow-lift"
            >
              Start Staging
            </button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-white/90 hover:text-white transition-colors"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-alabaster/95 backdrop-blur-xl border-b border-stone shadow-soft-lg max-h-[80vh] overflow-y-auto">
            <nav className="max-w-7xl mx-auto px-6 py-6 space-y-4">
              <Link
                to="/virtual-staging"
                onClick={() => setMobileMenuOpen(false)}
                className="block text-sand hover:text-olive text-base font-medium transition-colors"
              >
                Virtual Staging
              </Link>
              <Link
                to="/virtual-staging/styles"
                onClick={() => setMobileMenuOpen(false)}
                className="block text-sand hover:text-olive text-base font-medium transition-colors"
              >
                Styles
              </Link>
              <Link
                to="/virtual-staging/before-after"
                onClick={() => setMobileMenuOpen(false)}
                className="block text-sand hover:text-olive text-base font-medium transition-colors"
              >
                Before/After
              </Link>
              <Link
                to="/virtual-staging/pricing"
                onClick={() => setMobileMenuOpen(false)}
                className="block text-sand hover:text-olive text-base font-medium transition-colors"
              >
                Pricing
              </Link>
              
              {/* Solutions Section */}
              <div className="pt-4 border-t border-stone">
                <div className="text-xs font-mono uppercase tracking-wider text-sand/60 mb-3">
                  Solutions by Role
                </div>
                <div className="space-y-3 pl-2">
                  <Link
                    to="/virtual-staging/real-estate-agents"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block text-sand hover:text-olive text-sm transition-colors"
                  >
                    Real Estate Agents
                  </Link>
                  <Link
                    to="/virtual-staging/photographers"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block text-sand hover:text-olive text-sm transition-colors"
                  >
                    Photographers
                  </Link>
                  <Link
                    to="/virtual-staging/broker-teams"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block text-sand hover:text-olive text-sm transition-colors"
                  >
                    Broker Teams
                  </Link>
                </div>
              </div>
              
              {/* Partners Section */}
              <div className="pt-4 border-t border-stone">
                <div className="text-xs font-mono uppercase tracking-wider text-sand/60 mb-3">
                  Partners
                </div>
                <div className="space-y-3 pl-2">
                  <Link
                    to="/partners/photographers"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block text-sand hover:text-olive text-sm transition-colors"
                  >
                    Photographer Partners
                  </Link>
                  <Link
                    to="/partners/brokerages"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block text-sand hover:text-olive text-sm transition-colors"
                  >
                    Brokerage Partners
                  </Link>
                </div>
              </div>
              
              {/* Auth Buttons */}
              <div className="pt-4 border-t border-stone space-y-3">
                <Link
                  to="/auth"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block text-center px-6 py-3 text-sand hover:text-charcoal border border-stone rounded-full text-sm font-medium transition-colors"
                >
                  Log in
                </Link>
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    onGetStarted();
                  }}
                  className="block w-full text-center px-6 py-3 bg-charcoal hover:bg-olive text-alabaster rounded-full text-sm font-medium transition-colors"
                >
                  Start Staging
                </button>
              </div>
            </nav>
          </div>
        )}
      </nav>
    </>
  );
}

export default LandingNav;

